/**
 * Previsione tasse in regime forfettario (L. 190/2014).
 *
 * NB: è solo una STIMA. I parametri reali (coefficiente di redditività, aliquota
 * imposta sostitutiva 5%/15%, gestione previdenziale INPS) dipendono dal codice
 * ATECO e dalla situazione del contribuente: per questo sono modificabili nella UI.
 *
 * Schema di calcolo (principio di cassa — si tassa l'incassato):
 *   reddito lordo      = base imponibile incassata × coefficiente di redditività
 *   contributi INPS    = reddito lordo × aliquota contributiva
 *   reddito imponibile = reddito lordo − contributi INPS (deducibili)
 *   imposta sostitutiva= reddito imponibile × aliquota imposta (5% o 15%)
 *   totale da accantonare = imposta sostitutiva + contributi INPS
 */

export interface ForfettarioParams {
  /**
   * Coefficiente di redditività in % (dipende dall'ATECO).
   * Agenzia/sviluppo software ATECO 62 (62.10 produzione software, 62.20 consulenza
   * informatica) e gruppo 58-63 → 67% ("altre attività economiche").
   * NB: il 78% vale per attività professionali/tecniche (ATECO 64-66, 69-75, 85-88),
   * che NON comprendono lo sviluppo software. Coefficienti invariati dal 2015.
   */
  coefficiente: number;
  /** Aliquota imposta sostitutiva in %: 5 (start-up primi 5 anni) o 15 (a regime). */
  aliquotaImposta: number;
  /** Aliquota contributiva INPS in %. Es. 26.07 Gestione Separata, ~24 Artigiani/Commercianti. */
  aliquotaInps: number;
}

export const DEFAULT_FORFETTARIO_PARAMS: ForfettarioParams = {
  // 67% = gruppo ATECO 58-63 (incl. 62 produzione software / consulenza informatica)
  coefficiente: 67,
  aliquotaImposta: 5,
  aliquotaInps: 26.07,
};

export const FORFETTARIO_PARAMS_STORAGE_KEY = 'analisi_forfettario_params';

export interface ForfettarioForecast {
  /** Base imponibile usata per il calcolo (incassato o emesso). */
  base: number;
  redditoLordo: number;
  contributiInps: number;
  redditoImponibile: number;
  impostaSostitutiva: number;
  /** Imposta sostitutiva + contributi INPS. */
  totale: number;
  /** % della base che andrebbe accantonata per coprire tasse + contributi. */
  pressioneEffettiva: number;
}

export function computeForfettario(base: number, p: ForfettarioParams): ForfettarioForecast {
  const safeBase = Number.isFinite(base) && base > 0 ? base : 0;
  const redditoLordo = safeBase * (p.coefficiente / 100);
  const contributiInps = redditoLordo * (p.aliquotaInps / 100);
  const redditoImponibile = Math.max(0, redditoLordo - contributiInps);
  const impostaSostitutiva = redditoImponibile * (p.aliquotaImposta / 100);
  const totale = impostaSostitutiva + contributiInps;
  return {
    base: safeBase,
    redditoLordo,
    contributiInps,
    redditoImponibile,
    impostaSostitutiva,
    totale,
    pressioneEffettiva: safeBase > 0 ? (totale / safeBase) * 100 : 0,
  };
}

/**
 * Calendario versamenti F24 (persona fisica / forfettario, gestione separata INPS).
 * Stima a regime: l'imposta+contributi dovuti per l'anno si versano come acconto/saldo
 * alle due scadenze ordinarie. Soglie acconto imposta sostitutiva:
 *  - dovuto ≤ 51,65 € → nessun acconto;
 *  - acconto ≤ 257,52 € → unica rata a novembre;
 *  - acconto > 257,52 € → 40% a giugno + 60% a novembre.
 * NB: stima — saldo reale e acconti dipendono dall'anno precedente; verifica col commercialista.
 */
export const ACCONTO_MINIMO = 51.65;
export const ACCONTO_RATA_UNICA_MAX = 257.52;

export interface F24Rata {
  date: string;
  label: string;
  detail: string;
  amount: number;
}

export function computeF24Schedule(totale: number, year: number): F24Rata[] {
  const t = Number.isFinite(totale) && totale > 0 ? totale : 0;
  if (t <= 0) return [];
  // Sotto il minimo: nessun acconto, di fatto si regola tutto a saldo l'anno dopo.
  if (t <= ACCONTO_MINIMO) {
    return [{ date: `30 giugno ${year + 1}`, label: 'Saldo', detail: `Saldo ${year} (importo modesto)`, amount: t }];
  }
  // Acconto piccolo: rata unica a novembre.
  if (t <= ACCONTO_RATA_UNICA_MAX) {
    return [{ date: `30 novembre ${year}`, label: 'Acconto unico', detail: 'Rata unica (acconto ≤ 257,52 €)', amount: t }];
  }
  // Caso ordinario: 40% giugno + 60% novembre.
  return [
    { date: `30 giugno ${year}`, label: '1ª rata (40%)', detail: 'Saldo anno prec. + 1º acconto', amount: t * 0.4 },
    { date: `30 novembre ${year}`, label: '2ª rata (60%)', detail: '2º acconto', amount: t * 0.6 },
  ];
}

export function loadForfettarioParams(): ForfettarioParams {
  try {
    const raw = localStorage.getItem(FORFETTARIO_PARAMS_STORAGE_KEY);
    if (!raw) return DEFAULT_FORFETTARIO_PARAMS;
    const parsed = JSON.parse(raw);
    return {
      coefficiente: Number(parsed.coefficiente) || DEFAULT_FORFETTARIO_PARAMS.coefficiente,
      aliquotaImposta: Number(parsed.aliquotaImposta) || DEFAULT_FORFETTARIO_PARAMS.aliquotaImposta,
      aliquotaInps: Number(parsed.aliquotaInps) || DEFAULT_FORFETTARIO_PARAMS.aliquotaInps,
    };
  } catch {
    return DEFAULT_FORFETTARIO_PARAMS;
  }
}

export function saveForfettarioParams(p: ForfettarioParams): void {
  try {
    localStorage.setItem(FORFETTARIO_PARAMS_STORAGE_KEY, JSON.stringify(p));
  } catch {
    /* ignore */
  }
}
